/**
 * The console's Rollouts section (container, D40 phase 7f part B): the current rollout and its rings' gates
 * (`RolloutPanel`) and the confirm dialog for the vendor's actions (`RolloutActionDialog`). Live through
 * `releaseRollout` / `releaseRing` changes (a publish, the gate opening a ring, another vendor's action), and re-read
 * every minute (a soak runs out, and a node's report moves a gate, with no change the rollout itself announces).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { useRolloutAction, useVendorRollout } from '../../app/hooks.js';
import { RolloutActionDialog, type RolloutActionTarget } from '../../components/vendor/RolloutActionDialog.js';
import { RolloutPanel } from '../../components/vendor/RolloutPanel.js';
import { useVendorAdmin } from '../VendorLayout.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function ConsoleRolloutsView() {
  const rollout = useVendorRollout();
  const act = useRolloutAction();
  const canAdmin = useVendorAdmin();
  const [target, setTarget] = useState<RolloutActionTarget | null>(null);
  return (
    <div data-testid="console-rollouts">
      <RolloutPanel
        rollout={rollout.data ?? null}
        loading={rollout.isFetching}
        error={rollout.error === null ? null : messageOf(rollout.error)}
        canAdmin={canAdmin}
        onRetry={() => void rollout.refetch()}
        onAction={(action, listHash) => {
          act.reset();
          setTarget({ action, listHash });
        }}
      />
      <RolloutActionDialog
        target={target}
        pending={act.isPending}
        error={act.error === null ? null : messageOf(act.error)}
        onClose={() => setTarget(null)}
        onSubmit={(request) =>
          act.mutate(request, {
            onSuccess: (next) => {
              setTarget(null);
              toast.success(`The rollout is ${next.status}`);
            },
          })
        }
      />
    </div>
  );
}
